import { Button } from '@/components/ui/button';
import Title from '@/components/ui/Title';
import type { OfferResults, OfferTemplate } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { getBackgroundStyle, getBorderStyle, getReadableTextColor } from '@/utils/colors';
import { Award, BadgePercent, Trophy } from 'lucide-react';
import { useAppDispatch } from '@/app/hooks';
import { postAnalyticsEvent } from '@/features/live/liveThunks';
import { useChannelName } from '@/routes/AblyAuth';
import type { Message } from 'ably';
import AblyChannelListener from '@/components/common/AblyChannelListener';
import { parsePresenceUpdateLiveCount } from '@/lib/presenceUpdate';
import { useState, useEffect, useRef } from 'react';
import { useWebinarT } from '@/hooks/useWebinarT';
import {
  opensInOfferPanel,
  resolveOfferEmbedTabUrl,
  resolveOfferExternalUrl,
} from '@/lib/offerEmbed';

function mergeOfferResultsPayload(
  partial: Record<string, unknown>,
  base: OfferResults | null | undefined,
  fallbackOfferId: number,
  fallbackWebinarId: number | undefined,
): OfferResults {
  const toNum = (v: unknown, fallback: number): number => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    abTesting: Boolean(partial.abTesting ?? base?.abTesting ?? false),
    clicksA: toNum(partial.clicksA, base?.clicksA ?? 0),
    clicksB: toNum(partial.clicksB, base?.clicksB ?? 0),
    purchasesA: toNum(partial.purchasesA, base?.purchasesA ?? 0),
    purchasesB: toNum(partial.purchasesB, base?.purchasesB ?? 0),
    assignmentsA: toNum(partial.assignmentsA, base?.assignmentsA ?? 0),
    assignmentsB: toNum(partial.assignmentsB, base?.assignmentsB ?? 0),
    totalClicks: toNum(partial.totalClicks, base?.totalClicks ?? 0),
    totalPurchases: toNum(
      partial.totalPurchases ?? partial.totalPurchase,
      base?.totalPurchases ?? 0,
    ),
    presentClicks: toNum(partial.presentClicks, base?.presentClicks ?? 0),
    presentPurchases: toNum(partial.presentPurchases, base?.presentPurchases ?? 0),
    offerId: fallbackOfferId,
    webinarId:
      partial.webinarId != null && Number.isFinite(Number(partial.webinarId))
        ? Number(partial.webinarId)
        : (fallbackWebinarId ?? base?.webinarId),
  };
}

function OfferResultsInside({
  results,
  liveParticipantCount,
  visualIdentity,
}: {
  results: OfferResults;
  liveParticipantCount: number;
  visualIdentity: WorkspaceVisualIdentity;
}) {
  const { t } = useWebinarT('webinars');
  const { totalClicks, totalPurchases, presentClicks, presentPurchases } = results;

  const background = visualIdentity.backgroundColor || '#FFFFFF';
  const secondaryBackground = `${background}E6`;
  const contrast = visualIdentity.contrastColor + `40` || '#FFFFFF';
  const textColor = getReadableTextColor(background);

  // Les comptes affichés restent globaux (totalClicks/totalPurchases),
  // mais les pourcentages se calculent sur les seuls spectateurs présents à l'instant T.
  const clicksPct =
    liveParticipantCount > 0
      ? Math.min(100, Math.round((presentClicks / liveParticipantCount) * 100))
      : null;
  const purchasesPct =
    liveParticipantCount > 0
      ? Math.min(100, Math.round((presentPurchases / liveParticipantCount) * 100))
      : null;

  return (
    <div
      className="mt-3 w-full rounded-2xl border border-solid px-3 py-3 sm:px-4 text-xs shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-[2px]"
      style={{
        color: textColor,
        borderColor: contrast,
        background: secondaryBackground,
      }}
    >
      <div className="flex items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight tracking-tight">
              {t('live.offerResults.title')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="rounded-xl px-3 py-2.5 text-center sm:text-left" style={{ background }}>
          <p className="text-[10px] uppercase tracking-wider font-medium opacity-80">
            {t('live.offerResults.clicks')}
          </p>
          <div className="flex items-center gap-2 justify-between">
            <p className="text-lg font-bold tabular-nums leading-tight mt-0.5">{totalClicks}</p>
            {clicksPct ? (
              <div className="flex items-center justify-between gap-2 w-full">
                <div
                  className="h-2 w-full overflow-hidden rounded-full"
                  style={{ background: `${textColor}33` }}
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{
                      width: `${clicksPct}%`,
                      background: textColor,
                    }}
                  />
                </div>
                <span className="text-[10px] uppercase tracking-wider font-medium opacity-80">
                  {clicksPct}%
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="rounded-xl px-3 py-2.5 text-center sm:text-left" style={{ background }}>
          <p className="text-[10px] uppercase tracking-wider font-medium opacity-80">
            {t('live.offerResults.conversions')}
          </p>
          <div className="flex items-center gap-2 justify-between">
            <p className="text-lg font-bold tabular-nums leading-tight mt-0.5">{totalPurchases}</p>
            {purchasesPct ? (
              <div className="flex items-center justify-between gap-2 w-full">
                <div
                  className="h-2 w-full overflow-hidden rounded-full"
                  style={{ background: `${textColor}33` }}
                >
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{
                      width: `${purchasesPct}%`,
                      background: textColor,
                    }}
                  />
                </div>
                <span className="text-[10px] uppercase tracking-wider font-medium opacity-80">
                  {purchasesPct}%
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

type OfferCardProps = {
  offer: OfferTemplate;
  visualIdentity: WorkspaceVisualIdentity;
  attendeeView?: boolean;
  webinarId?: number;
  sessionIdentifier?: string;
  authorization?: string;
  isAdmin?: boolean;
  isModerator?: boolean;
  offerLive?: boolean;
  offerResults?: OfferResults | null;
  liveParticipantCountSync?: number;
  // Fourni là où le panneau d'embed peut s'afficher ; ailleurs, l'embed s'ouvre dans un onglet.
  onEmbedOpen?: () => void;
  // Salle de test : le clic reste local, sans lien, embed ni analytics.
  onLocalClick?: () => void;
};

export default function OfferCard({
  offer,
  visualIdentity,
  attendeeView = true,
  webinarId,
  sessionIdentifier,
  authorization,
  isAdmin = false,
  isModerator = false,
  offerLive = false,
  offerResults: offerResultsProp,
  liveParticipantCountSync = 0,
  onEmbedOpen,
  onLocalClick,
}: OfferCardProps) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const content = offer?.contents?.[0];
  const [offerResultsFromAbly, setOfferResultsFromAbly] = useState<OfferResults | null>(null);
  const [liveParticipantCountFromChannel, setLiveParticipantCountFromChannel] = useState(0);
  const offerLiveRef = useRef(offerLive);
  offerLiveRef.current = offerLive;
  const roleRef = useRef({ attendeeView, isAdmin, isModerator });
  roleRef.current = { attendeeView, isAdmin, isModerator };
  const offerResultsPropRef = useRef(offerResultsProp);
  offerResultsPropRef.current = offerResultsProp;

  useEffect(() => {
    if ((isAdmin || isModerator) && !offerLive) {
      setOfferResultsFromAbly(null);
    }
  }, [isAdmin, isModerator, offerLive]);

  const channelName = useChannelName();
  const handleChannelMessage = (message: Message) => {
    if (!channelName || !message) return;

    const normalizedMessages =
      message.name === 'batch-events' && Array.isArray(message.data)
        ? message.data.filter(
            (event): event is { name: string; data: unknown } =>
              !!event &&
              typeof event === 'object' &&
              typeof (event as { name?: unknown }).name === 'string',
          )
        : [{ name: message.name, data: message.data }];

    const { attendeeView: av, isAdmin: ia, isModerator: im } = roleRef.current;

    for (const currentMessage of normalizedMessages) {
      if (currentMessage.name === 'presence-update') {
        if (av || ia || im) {
          const live = parsePresenceUpdateLiveCount(currentMessage.data);
          if (live !== null) {
            setLiveParticipantCountFromChannel(live);
          }
        }
        continue;
      }
      if (currentMessage.name !== 'offer-results-update') continue;
      if (!offer?.id) continue;

      const raw = currentMessage.data;
      if (!raw || typeof raw !== 'object') continue;
      const partial = raw as Record<string, unknown>;

      const rawOfferId = partial.offerId ?? partial.resourceId;
      const payloadSpecifiesOffer =
        rawOfferId !== undefined &&
        rawOfferId !== null &&
        rawOfferId !== '' &&
        Number.isFinite(Number(rawOfferId));

      if (payloadSpecifiesOffer && Number(rawOfferId) !== Number(offer.id)) {
        continue;
      }

      if (
        webinarId != null &&
        partial.webinarId != null &&
        Number(partial.webinarId) !== Number(webinarId)
      ) {
        continue;
      }
      if (!payloadSpecifiesOffer && (ia || im) && !offerLiveRef.current) {
        continue;
      }

      const tc = Number(partial.totalClicks);
      const tp = Number(partial.totalPurchases ?? partial.totalPurchase);
      const hasNumericUpdate =
        Number.isFinite(tc) ||
        Number.isFinite(tp) ||
        Number.isFinite(Number(partial.clicksA)) ||
        Number.isFinite(Number(partial.clicksB)) ||
        Number.isFinite(Number(partial.purchasesA)) ||
        Number.isFinite(Number(partial.purchasesB));

      if (!hasNumericUpdate) continue;

      const applyMerge = () => {
        setOfferResultsFromAbly((prev) =>
          mergeOfferResultsPayload(
            partial,
            prev ?? offerResultsPropRef.current ?? null,
            offer.id,
            webinarId,
          ),
        );
      };

      if (ia || im || av) {
        applyMerge();
      }
    }
  };
  const channelListener = channelName ? (
    <AblyChannelListener channelName={channelName} onMessage={handleChannelMessage} />
  ) : null;

  const effectiveOfferResults = offerLive
    ? (offerResultsFromAbly ?? offerResultsProp ?? null)
    : (offerResultsProp ?? null);

  const liveParticipantCount = Math.max(
    liveParticipantCountSync ?? 0,
    liveParticipantCountFromChannel,
  );

  const canSeeResults = (isAdmin || isModerator) && !!effectiveOfferResults;

  if (!content) {
    return channelListener;
  }

  return (
    <div
      className=" flex flex-col md:flex-row lg:flex-col gap-1 md:gap-3 lg:gap-4 p-3 lg:p-4 rounded-2xl h-fit lg:h-auto border-2 w-full"
      style={{
        ...getBackgroundStyle(content?.backgroundColor, undefined, visualIdentity),
        ...getBorderStyle(content?.borderColor, undefined, visualIdentity),
      }}
    >
      {channelListener}
      {content?.imageUrl && (
        <div className="w-full h-full hidden md:block">
          <img
            src={content?.imageUrl}
            alt={t('live.offerImageAlt')}
            loading="lazy"
            className="rounded-lg w-full min-h-[114px] md:h-[130px] lg:h-full lg:max-h-56 object-cover object-center"
          />
        </div>
      )}
      <div className={`flex flex-col justify-between items-center md:items-start gap-2 w-full`}>
        {content?.icon && (
          <div className="flex items-center justify-center">
            {content?.icon === 'percent' && <BadgePercent size={48} className="size-6" />}
            {content?.icon === 'award' && <Award size={48} className="size-6" />}
            {content?.icon === 'trophy' && <Trophy size={48} className="size-6" />}
          </div>
        )}
        <div className="flex flex-col justify-center items-center md:items-start gap-1 flex-2">
          <Title level={2} className="text-center md:text-left text-base lg:text-[20px]">
            <span style={{ color: content?.titleColor ?? '#ffffff' }}>{content?.title ?? ''}</span>
          </Title>
          <p
            className="text-center md:text-left text-sm "
            style={{ color: content?.textColor ?? '#ffffff' }}
          >
            {content?.subTitle ?? ''}
          </p>
          <p
            className="text-center md:text-left text-xs "
            style={{ color: content?.textColor ?? '#ffffff' }}
          >
            {content?.description ?? ''}
          </p>
        </div>
        <div className="w-full lg:pt-3 flex-1">
          <Button
            className="w-full justify-start! inline! md:inline-flex! md:justify-center! truncate!"
            style={{
              backgroundColor: content?.buttonColor ?? '#2c6cff',
              color: content?.buttonTextColor ?? '#ffffff',
            }}
            onClick={() => {
              if (onLocalClick) {
                onLocalClick();
                return;
              }
              if (attendeeView) {
                if (webinarId && sessionIdentifier && authorization) {
                  dispatch(
                    postAnalyticsEvent({
                      webinarId,
                      sessionIdentifier,
                      authorization,
                      input: {
                        eventType: 'OFFER_CLICK',
                        payloadJson: JSON.stringify({
                          offerId: offer?.id,
                        }),
                        occurredAt: new Date().toISOString(),
                      },
                    }),
                  );
                }
                if (opensInOfferPanel(content)) {
                  if (onEmbedOpen) {
                    onEmbedOpen();
                    return;
                  }
                  const tabUrl = resolveOfferEmbedTabUrl(content);
                  if (tabUrl) window.open(tabUrl, '_blank', 'noopener,noreferrer');
                  return;
                }
                const url = resolveOfferExternalUrl(content?.buttonLink);
                if (url) window.open(url, '_blank');
              }
            }}
          >
            {content?.buttonLabel ?? ''}
          </Button>
        </div>

        {canSeeResults && effectiveOfferResults ? (
          <OfferResultsInside
            results={effectiveOfferResults}
            liveParticipantCount={liveParticipantCount}
            visualIdentity={visualIdentity}
          />
        ) : null}
      </div>
    </div>
  );
}
